export type SurveyCondition = {
  questionId: string;
  optionIds: string[];
};

export type ConditionalQuestion = {
  id?: string;
  key?: string;
  displayWhen?: SurveyCondition | null;
  sectionId?: string | null;
};

export type SurveyValue = string | string[] | null | undefined;
export type QuestionOutline = { label: string; depth: number; root: string };

export const surveyQuestionNumber = (label: string) =>
  label.includes(".") ? label : `${label}.`;

const identity = (value: string) => value.replace(/-/g, "").toLowerCase();
const keyOf = (question: ConditionalQuestion) =>
  question.key ?? question.id ?? "";

function letters(index: number): string {
  let result = "";
  for (let value = index + 1; value > 0; value = Math.floor((value - 1) / 26)) {
    result = String.fromCharCode(65 + ((value - 1) % 26)) + result;
  }
  return result;
}

export function surveyOutline(questions: ConditionalQuestion[]) {
  const result = new Map<string, QuestionOutline>();
  const children = new Map<string, number>();
  let roots = 0;
  for (const question of questions) {
    const key = keyOf(question);
    const parentKey = question.displayWhen?.questionId;
    const parent = parentKey ? result.get(identity(parentKey)) : undefined;
    if (!parent) {
      roots += 1;
      result.set(identity(key), { label: String(roots), depth: 0, root: key });
    } else {
      const index = children.get(identity(parentKey!)) ?? 0;
      children.set(identity(parentKey!), index + 1);
      result.set(identity(key), {
        label: `${parent.label}.${parent.depth % 2 === 0 ? letters(index) : index + 1}`,
        depth: parent.depth + 1,
        root: parent.root,
      });
    }
  }
  return {
    get: (key: string) =>
      result.get(identity(key)) ?? { label: "", depth: 0, root: key },
  };
}

export function visibleSurveyQuestions<T extends ConditionalQuestion>(
  questions: T[],
  answers: Record<string, SurveyValue>,
): T[] {
  const visible = new Set<string>();
  const values = new Map(
    Object.entries(answers).map(([key, value]) => [identity(key), value]),
  );
  return questions.filter((question) => {
    const condition = question.displayWhen;
    let show = !condition;
    if (condition && visible.has(identity(condition.questionId))) {
      const value = values.get(identity(condition.questionId));
      const selected = Array.isArray(value) ? value : value ? [value] : [];
      const matches = new Set(selected.map(identity));
      show = condition.optionIds.some((option) =>
        matches.has(identity(option)),
      );
    }
    if (show) visible.add(identity(keyOf(question)));
    return show;
  });
}

export function surveyFamilyPages<T extends ConditionalQuestion>(
  questions: T[],
  pageSize = 10,
): T[][] {
  const outline = surveyOutline(questions);
  const pages: T[][] = [];
  let root = "";
  let count = 0;
  for (const question of questions) {
    const nextRoot = outline.get(keyOf(question)).root;
    if (nextRoot !== root) {
      if (count % pageSize === 0) pages.push([]);
      count += 1;
      root = nextRoot;
    }
    pages[pages.length - 1].push(question);
  }
  return pages.length ? pages : [[]];
}

export function hasSurveyAnswer(value: SurveyValue): boolean {
  return Array.isArray(value)
    ? value.length > 0
    : typeof value === "string" && value.trim().length > 0;
}

export function questionFamily<T extends ConditionalQuestion>(
  questions: T[],
  key: string,
): T[] {
  const keys = new Set([identity(key)]);
  return questions.filter((question) => {
    const own = identity(keyOf(question));
    if (
      question.displayWhen &&
      keys.has(identity(question.displayWhen.questionId))
    )
      keys.add(own);
    return keys.has(own);
  });
}

export function reorderQuestionFamily<T extends ConditionalQuestion>(
  questions: T[],
  from: string,
  to: string,
): T[] {
  const active = questions.find((q) => identity(keyOf(q)) === identity(from));
  const target = questions.find((q) => identity(keyOf(q)) === identity(to));
  if (
    !active ||
    !target ||
    (active.displayWhen?.questionId ?? "") !==
      (target.displayWhen?.questionId ?? "") ||
    (!active.displayWhen &&
      (active.sectionId ?? null) !== (target.sectionId ?? null))
  )
    return questions;
  const moving = questionFamily(questions, from);
  const movingKeys = new Set(moving.map(keyOf));
  const remaining = questions.filter((q) => !movingKeys.has(keyOf(q)));
  const targetFamily = questionFamily(remaining, to);
  const forward = questions.indexOf(active) < questions.indexOf(target);
  const index =
    remaining.indexOf(
      forward ? targetFamily[targetFamily.length - 1] : target,
    ) + (forward ? 1 : 0);
  return [...remaining.slice(0, index), ...moving, ...remaining.slice(index)];
}

export type SurveySection = {
  id: string;
  title: string;
  description: string | null;
  position: number;
};

export function surveySectionPages<
  T extends ConditionalQuestion & { sectionId?: string | null },
>(
  questions: T[],
  sections: SurveySection[],
): { section: SurveySection | null; questions: T[] }[] {
  if (!sections.length)
    return surveyFamilyPages(questions).map((page) => ({
      section: null,
      questions: page,
    }));
  const outline = surveyOutline(questions);
  const byKey = new Map(questions.map((q) => [keyOf(q), q]));
  return [...sections]
    .sort((a, b) => a.position - b.position)
    .map((section) => ({
      section,
      questions: questions.filter(
        (q) => byKey.get(outline.get(keyOf(q)).root)?.sectionId === section.id,
      ),
    }))
    .filter((page) => page.questions.length > 0);
}

export function orderSectionQuestions<
  T extends ConditionalQuestion & { sectionId?: string | null },
>(questions: T[], sections: SurveySection[]): T[] {
  if (!sections.length) return questions;
  const ordered = surveySectionPages(questions, sections).flatMap(
    (page) => page.questions,
  );
  // Keep unassigned families in the draft until they can be assigned again.
  const assigned = new Set(ordered);
  return [
    ...ordered,
    ...questions.filter((question) => !assigned.has(question)),
  ];
}

export type SurveyDraftSection = SurveySection & {
  key: string;
  persisted?: boolean;
};
